import { CONTENT, type ContentMap } from '~/data/contentLocales'
import { LOCALES, MESSAGES, type Locale, type Messages } from '~/data/locales'

/**
 * 🌍 Locale state (v2.88, per Javi). Meta progress, like stardust and the
 * codex: one localStorage key, loaded once, independent of run saves.
 *
 * First visit guesses from the browser (`navigator.language`) rather than
 * forcing English on a Spanish or French player, then remembers whatever
 * they actually pick.
 */
const STORAGE_KEY = 'elemental-dispatch-locale'

/** 🌍 v6.63: the non-English dictionaries are lazy chunks (data/lang/*),
 * loaded once per locale and registered into MESSAGES / CONTENT. The
 * revision counter is what makes t() / tc() re-render when one lands. */
const DICTIONARIES: Record<Exclude<Locale, 'en'>, () => Promise<[{ default: Messages }, { default: ContentMap }]>> = {
  es: () => Promise.all([import('~/data/lang/es'), import('~/data/lang/es-content')]),
  fr: () => Promise.all([import('~/data/lang/fr'), import('~/data/lang/fr-content')]),
}
const loading = new Map<Locale, Promise<void>>()

function detect(): Locale {
  if (typeof navigator === 'undefined') return 'en'
  for (const tag of navigator.languages ?? [navigator.language]) {
    const base = (tag ?? '').slice(0, 2).toLowerCase()
    if ((LOCALES as readonly string[]).includes(base)) return base as Locale
  }
  return 'en'
}

export function useLocale() {
  const locale = useState<Locale>('locale', () => 'en')
  const loaded = useState('locale-loaded', () => false)
  const dictRev = useState('locale-dict-rev', () => 0)

  function load() {
    if (loaded.value || typeof localStorage === 'undefined') return
    loaded.value = true
    const stored = localStorage.getItem(STORAGE_KEY)
    locale.value = (LOCALES as readonly string[]).includes(stored ?? '')
      ? (stored as Locale)
      : detect()
  }

  /** Fetch and register a locale's dictionaries (no-op for English or
   * once loaded). The locale plugin awaits this before the app mounts;
   * a failed load leaves English showing and lets the next call retry. */
  async function loadDictionary(l: Locale): Promise<void> {
    if (l === 'en' || MESSAGES[l]) return
    if (!loading.has(l)) {
      loading.set(l, DICTIONARIES[l]().then(([m, c]) => {
        MESSAGES[l] = m.default
        CONTENT[l] = c.default
        dictRev.value++
      }).catch((error) => {
        loading.delete(l)
        console.warn(`Dictionary for ${l} unavailable - English until it loads.`, error)
      }))
    }
    await loading.get(l)
  }

  function setLocale(next: Locale) {
    load()
    locale.value = next
    if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, next)
    if (typeof document !== 'undefined') document.documentElement.lang = next
    void loadDictionary(next)
  }

  /**
   * Looks up a key, falling back to English then to the key itself.
   * `{name}` placeholders are filled from `vars`.
   *
   * The English fallback is deliberate: a key missing from es/fr shows
   * readable English instead of a raw `menu.startRun`, so shipping a
   * partial translation degrades gracefully - and a dictionary still on
   * its way reads as English until it lands.
   */
  function t(key: string, vars?: Record<string, string | number>): string {
    load()
    void dictRev.value // subscribe: re-render when a dictionary lands
    const raw = MESSAGES[locale.value]?.[key] ?? MESSAGES.en[key] ?? key
    if (!vars) return raw
    return raw.replace(/\{(\w+)\}/g, (_, name) => String(vars[name] ?? `{${name}}`))
  }

  /**
   * Gameplay CONTENT lookup (v2.89): relic/reward/skill names and
   * descriptions, keyed by entity id. `fallback` is the English string
   * straight from the data file, which stays the source of truth — so an
   * untranslated entry reads as English rather than vanishing.
   */
  function tc(kind: string, id: string, field: string, fallback: string): string {
    load()
    const l = locale.value
    if (l === 'en') return fallback
    void dictRev.value
    return CONTENT[l]?.[`${kind}.${id}.${field}`] ?? fallback
  }

  return { locale, load, setLocale, loadDictionary, t, tc }
}
